/**
 * Tribes topologies — Track R (UX rewrite).
 *
 * Pure random in-group friend / out-group enemy is Schelling segregation:
 * two point-clumps, enemy edges collapse into one bar. Showcase structure:
 *
 *   friends  = Kirma-style **ring** within each type (cycles → living loops)
 *   enemies  = **paired rival** by rank across the other type (readable bridges)
 *
 * Types still obey tribes_fe allow rules (in-group F / out-group E for any T);
 * sampling is structural, not uniform random among eligible.
 */

import {
  assertTribesViable,
  assignTypesBlocks,
  assignTypesRandom,
  expandTypeRules,
  typeCounts,
} from "../../sim/typeRules";
import type { SimParams } from "../../sim/params";
import type { Rng } from "../../sim/rng";
import type { TopologyPlugin } from "../types";

/** Indices of each type in ascending index order (stable ring order). */
function indicesByType(types: Uint8Array, T: number): number[][] {
  const buckets: number[][] = Array.from({ length: T }, () => []);
  for (let i = 0; i < types.length; i++) {
    buckets[types[i]!]!.push(i);
  }
  return buckets;
}

/**
 * Build planar targets: in-type friend rings + cross-type paired enemies.
 * For T=2: enemy of type0[k] = type1[k % n1], and type1[k] → type0[k % n0]
 * (slight rank skew via ringStride so bridges aren't perfectly parallel).
 */
export function fillTribesStructural(
  types: Uint8Array,
  T: number,
  ringStride: number,
): Int32Array {
  const n = types.length;
  const R = 2;
  const targets = new Int32Array(n * R);
  const byType = indicesByType(types, T);
  const stride = Math.max(1, ringStride | 0);

  // rank within type + type id lookup
  const rankOf = new Int32Array(n);
  const typeOf = types;
  for (let t = 0; t < T; t++) {
    const list = byType[t]!;
    for (let r = 0; r < list.length; r++) {
      rankOf[list[r]!] = r;
    }
  }

  for (let i = 0; i < n; i++) {
    const t = typeOf[i]!;
    const mine = byType[t]!;
    const mlen = mine.length;
    if (mlen === 0) {
      targets[i] = i;
      targets[n + i] = i;
      continue;
    }
    const rSelf = rankOf[i]!;
    // Friend: next along in-type ring (Kirma)
    const fRank = (rSelf + stride) % mlen;
    targets[i] = mine[fRank]!;

    // Enemy: paired rank on "opposite" type (T=2: 1-t; T>2: (t+1)%T)
    const tOpp = T === 2 ? 1 - t : (t + 1) % T;
    const opp = byType[tOpp]!;
    if (opp.length === 0) {
      targets[n + i] = mine[(rSelf + 1) % mlen]!;
    } else {
      // Mild skew so bridges fan instead of stacking into one bar
      const eRank = (rSelf * stride + t) % opp.length;
      targets[n + i] = opp[eRank]!;
    }
  }

  return targets;
}

function tribesInit(
  params: SimParams,
  rng: Rng,
  mode: "blocks" | "random",
): { targets: Int32Array; types: Uint8Array } {
  const n = params.n;
  const T = params.typeCount;
  assertTribesViable(n, T);
  // Validate rules exist (two_tribes_fe etc.) — structure still enforces in/out group
  expandTypeRules(params);

  const types =
    mode === "blocks" ? assignTypesBlocks(n, T) : assignTypesRandom(n, T, rng);

  // Sanity: counts non-empty
  const counts = typeCounts(n, T);
  for (let t = 0; t < T; t++) {
    if (counts[t]! < 2 && params.forbidSelfLinks) {
      // ring needs ≥2 for nontrivial friend; allow but friend may be only other
    }
  }

  const targets = fillTribesStructural(types, T, params.ringStride);
  return { targets, types };
}

export const tribesBlocksTopology: TopologyPlugin = {
  id: "tribes_blocks",
  label: "Tribes (rings · blocks)",
  tier: "lab",
  description:
    "Contiguous type blocks; friend = in-type ring, enemy = paired cross-type rival. Pair with two_camps init.",
  init(params, rng) {
    return tribesInit(params, rng, "blocks");
  },
};

export const tribesRandomTopology: TopologyPlugin = {
  id: "tribes_random",
  label: "Tribes (rings · shuffled)",
  tier: "lab",
  description:
    "Shuffled types; friend = in-type ring in shuffle order, enemy = paired rival.",
  init(params, rng) {
    return tribesInit(params, rng, "random");
  },
};
